<template>
  <ten-popup
    trigger-type="manual"
    direction="bottom left"
    :destroy="false"
    :show="popupShow"
    :width="popupWidthHandler"
    :popup-class="_popupClass"
    @change="onPopupChange"
  >
    <template v-slot:trigger>
      <ten-input-select
        ref="input"
        :multiple="multiple"
        :class="_inputClass"
        :placeholder="placeholder"
        :is-filtering-mode="isFilteringMode"
        :disabled="disabled"
        :readonly="_readonly"
        :value="dataValue"
        :size="size"
        :opened="popupShow"
        :get-input="getInput"
        :get-value-key="v => getKey(v)"
        :get-value-text="v => getText(v)"
        :clearable="clearable"
        @tag-delete="onTagDelete"
        @text="onFilter"
        @click="onTriggerClick"
        @focus="onInputFocus"
        @blur="onInputBlur"
        @clear="onClear"
      >
        <template v-slot:suffixIcon>
          <ten-icon
            icon="carret_down"
            @click.stop="onSuffixIconClick"
          />
        </template>
      </ten-input-select>
    </template>

    <template v-slot:default>
      <div class="ten-tree-select__tree">
        <ten-tree
          ref="tree"
          :close-siblings="true"
          v-bind="$attrs"
          :data="data"
          :selected="multiple ? dataValue : dataValue[0]"
          :selected-mode="valueMode"
          :expanded.sync="expanded"
          selected-type="data"
          selectable
          :multiple="multiple"
          :node-key="nodeKey"
          :node-text="nodeText"
          :draggable="false"
          @selectedChange="onSelectedChange"
          @update:expanded="onExpandedChange"
          @click="onPopupClick"
        />
      </div>
      <slot v-if="!loading && !data.length" name="emptyTip">
        <div class="ten-tree-select__empty">{{emptyTip}}</div>
      </slot>
    </template>
  </ten-popup>
</template>
<script>
import Emitter from '../scripts/mixins/emitter';
import TenPopup from '../popup';
import TenInputSelect from '../input/input-select';
import TenIcon from '../icon';
import TenTree from '../tree';
import { isEmpty } from '../scripts/utils/helper';

export default {
  name: 'TenTreeSelect',
  components: {
    TenInputSelect,
    TenPopup,
    TenIcon,
    TenTree,
  },
  mixins: [Emitter],
  inject: {
    formItem: {
      from: 'tenFormItem',
      default: null,
    },
  },
  provide() {
    return {
      tenFormItem: undefined,
    };
  },
  props: {
    /**
     * v-model 选择器的当前值，对应事件 change
     */
    value: [Number, String, Object, Array],
    /**
     * 选择器的默认值
     */
    defalutValue: [Number, String, Object, Array],
    /**
     * value 可以是 key 或者是对象的形式
     * @member key | data
     */
    valueType: String,
    /**
     * valueMode 同 tree selectedMode
     * @member  all | onlyLeaf | parentFirst
     */
    valueMode: {
      type: String,
      default: 'all',
    },
    /**
     * 单选模式，是否可以清空
     */
    clearable: Boolean,
    /**
     * 输入提示
     */
    placeholder: {
      type: String,
      default: '请选择',
    },
    /**
     * 大小
     * @defaultValue default
     * @member default|small
     */
    size: {
      type: String,
      default: 'default',
    },
    /**
     * 禁用
     */
    disabled: Boolean,
    /**
     * 只读
     */
    readonly: Boolean,
    /**
     * data 树形数据
     */
    data: {
      type: Array,
      default: () => [],
    },
    /**
     * @ignore
     * todo: test
     * 设置为 true 则根据 nodeText 的值自动筛选
     * 设置为 false 不作过滤操作
     * 设置为函数，函数接受 nodeData 作为参数，函数返回 true or false 确定是否过滤该选项
     */
    filterNode: { type: [Boolean, Function], default: false },
    multiple: Boolean,
    /**
     * 节点 key(id) 在 nodeData 中对应的键名
     */
    nodeKey: {
      type: String,
      default: 'key',
    },
    /**
     * 节点文本在 nodeData 中对应的键名
     */
    nodeText: {
      type: String,
      default: 'text',
    },
    /**
     * 节点 children 在 nodeData 中对应的键名
     */
    nodeChildren: {
      type: String,
      default: 'children',
    },
    /**
     * 是否加载中，异步获取选项时适用
     */
    loading: { type: Boolean, default: false },
    /**
     * 结果为空时提示文字，支持同名slot
     */
    emptyTip: { type: String, default: '暂无数据' },
  },
  data() {
    return {
      popupShow: false,
      filterValue: '',
      dataValue: undefined,
      expanded: [],
    };
  },
  computed: {
    _inputClass() {
      return [
        'ten-tree',
        'ten-select-input',
        {
          'ten-select-input--popupshow': this.popupShow,
        },
      ];
    },
    _popupClass() {
      return [
        'ten-tree-select__popup',
        {
          'ten-tree-select__popup--loading': this.loading,
        },
      ];
    },
    _readonly() {
      return this.readonly || !(this.isFilteringMode && this.popupShow);
    },
    isFilteringMode() {
      return Boolean(!!this.filterOption || this.$listeners.filter);
    },
    // 筛选的时候，tree 接受的 data 并不是全部的 data，所以需要保存一份全量的 map
    _dataKeyMap() {
      const keyMap = {};
      this.walkData(this.data, (node) => {
        const key = this.getKey(node);
        keyMap[key] = node;
      });
      if (this.valueType === 'data' && this.dataValue) {
        const valueData = this.multiple ? this.dataValue : [this.dataValue];
        valueData.forEach((node) => {
          const key = this.getKey(node);
          keyMap[key] = node;
        });
      }
      return keyMap;
    },
    _filteredData() {
      return this.isFilteringMode ? this.filterData(this.data) : this.data;
    },
  },
  watch: {
    value: {
      handler(v) {
        this.dataValue = this.transform(v);
        this.expanded = [...this.expanded, ...this.dataValue.map(data => this.getKey(data))];
      },
      immediate: true,
    },
    data(v) {
      this.filteredData = v;
      this.dataValue = this.transform(this.value);
    },
  },
  created() {
    this.disableEmit = false;
  },
  methods: {
    transform(v) {
      let dataValue = v;
      if (!this.multiple || (this.multiple && !Array.isArray(dataValue))) {
        dataValue = !isEmpty(v) ? [dataValue] : [];
      }
      if (this.valueType !== 'data') {
        dataValue = dataValue
          .map(key => this._dataKeyMap[key] || '')
          .filter(v => v);
      }
      return dataValue;
    },
    reverse(v) {
      if (this.valueType === 'data') {
        return this.multiple ? v : v[0];
      }

      let dataValue = v;
      dataValue = dataValue.map(item => this.getKey(item));
      return this.multiple ? dataValue : dataValue[0];
    },
    getKey(nodeData) {
      return nodeData[this.nodeKey];
    },
    getText(nodeData) {
      return nodeData[this.nodeText];
    },
    getChildren(nodeData) {
      return nodeData[this.nodeChildren];
    },
    walkData(data, fn) {
      if (Array.isArray(data)) {
        data.forEach(item => this.walkData(item, fn));
      } else {
        fn(data);
        const children = this.getChildren(data);
        if (children) {
          this.walkData(children, fn);
        }
      }
    },
    getDataByKey(key) {
      return this._dataKeyMap[key];
    },
    trigger() {
      this.focus();
      setTimeout(this.onTriggerClick);
    },
    focus() {
      this.disableEmit = true;
      setTimeout(() => {
        this.disableEmit = false;
      }, 10);
      this.refInput && this.refInput.focus();
    },
    blur() {
      this.refInput && this.refInput.blur();
      this.hidePopup();
    },
    updateValue(unreversedValue) {
      const reversedValue = this.reverse(unreversedValue);
      /**
       * value 值改变事件
       *
       * @event change
       * @param value
       */
      this.$emit('change', reversedValue);
      this.$nextTick(() => {
        this.formItem && this.formItem.$emit('form.change');
      });
    },
    onPopupClick() {
      this.focus(true);
    },
    onSelectedChange(selected) {
      this.updateValue(this.multiple ? selected : [selected]);
      if (this.multiple) {
        this.focus(true);
        this.filterValue = '';
      } else {
        this.hidePopup();
      }
    },
    onTagDelete(tagToDelete, afterDeleteTags) {
      const value = afterDeleteTags;

      this.focus();
      this.updateValue(value);
    },
    onExpandedChange() {
      this.focus(true);
    },
    getInput(el) {
      this.refInput = el;
    },
    onTriggerClick() {
      if (
        this.readonly
        || this.disabled
        || (this.isFilteringMode && this.popupShow)
      ) {
        return;
      }
      this.popupShow = !this.popupShow;
    },
    onSuffixIconClick() {
      if (this.readonly || this.disabled) return;
      this.popupShow = !this.popupShow;
    },
    onPopupChange() {
      this.hidePopup();
    },
    onInputFocus(e) {
      if (!this.disableEmit) {
        /**
         * 聚焦事件
         *
         * @event focus
         * @param {Object} e
         */
        this.$emit('focus', e);
      }
    },
    onInputBlur(e) {
      /**
       * 失焦事件
       *
       * @event blur
       * @param {Object} e
       */
      this.$emit('blur', e);
      this.dispatch('ten-form-item', 'form.blur', this.curValue);
      this.hidePopup();
    },
    onClear() {
      this.updateValue([]);
    },
    hidePopup() {
      this.popupShow = false;
      this.filterValue = '';
    },
    popupWidthHandler(input) {
      return input.offsetWidth;
    },
    onFilter(text) {
      this.filterValue = text.trim();
      /**
       * @ignore
       * 正在输入筛选值
       */
      this.$emit('filter', text);
    },
    getFilterData(node, fn) {
      if (Array.isArray(node)) {
        return node.filter(item => this.getFilterData(item, fn));
      }
      return fn(node);
    },

    filterData(data) {
      if (!this.filterValue || !this.filterNode) return data;

      if (typeof this.filterNode === 'function') {
        return this.getFilterData(this.data, node => this.filterNode(this.filterValue, node));
      }
      return this.getFilterData((node) => {
        const text = this.getText(node);
        return text.indexOf(text) > -1;
      });
    },
  },
};
</script>
